import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import type { ColourToolsState, ColourToolsActions, ColourTarget, ColorToolScope } from "../../../types";
import { useModalDismiss } from "../hooks/useModalDismiss";
import { normalizeHex, hexToRgbStr } from "../../../utils/colourUtils";
import { resolveColorToolScope, SCOPE_OPTION_LABELS } from "../../../utils/colorToolScope";
import { validateSelection } from "../../../utils/validateSelection";
import { logError, scrubMessage } from "../../../../../telemetry";
import styles from "./ReplaceColorsModal.module.css";

const REPLACE_CONNECTOR = "deckorate";

/** Report a ReplaceColorsModal failure through the shared telemetry pipeline
 * (PII-scrubbed) — mirrors NonBrandModal.tsx's `logNonBrandError` /
 * pptxBytes.ts's `logPptxError` convention. `tools.replace` already catches
 * and logs its own known failure modes (and sets `tools.error`) before ever
 * resolving, so this only fires for a genuinely unexpected exception
 * escaping that contract. */
function logReplaceError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: REPLACE_CONNECTOR, reason },
  );
}

interface ReplaceColorsModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tools: ColourToolsState & ColourToolsActions;
  readonly initialMappings?: Array<{ from: string; to: string }>;
}

interface ColorRow {
  from: string;
  to: string;
  checked: boolean;
  count: number;
  nearestKey: string | null;
}

function formatShapeCount(count: number): string {
  if (count <= 0) return "";
  return `${count} shape${count !== 1 ? "s" : ""}`;
}

const REPLACE_ALL_HEX_ERROR_ID = "replace-all-hex-error";

export function ReplaceColorsModal({
  open,
  onClose,
  tools,
  initialMappings,
}: ReplaceColorsModalProps) {
  const dialogRef = useModalDismiss<HTMLDivElement>(open, onClose);
  const [replaceAll, setReplaceAll] = useState(false);
  const [replaceAllHex, setReplaceAllHex] = useState("#ffffff");
  const [replaceAllRgbInput, setReplaceAllRgbInput] = useState("#ffffff");
  const [replaceTarget, setReplaceTarget] = useState<ColourTarget>("fill");
  // "Selected Shapes" is no longer a pickable "Apply to" option — a
  // selection is now a mandatory PRECONDITION for using Replace at all
  // (ColourToolsPanel's requireSelectionForReplace guards opening the modal,
  // and the polling effect below keeps enforcing it for as long as the
  // modal stays open). "Apply to" only decides how far a replace reaches
  // BEYOND that selected shape once a mapping's been picked — defaults to
  // "Current Slide" as the least-surprising broadening.
  const [replaceScope, setReplaceScope] = useState<ColorToolScope>("current");
  const [rangeFrom, setRangeFrom] = useState(1);
  const [rangeTo, setRangeTo] = useState(1);
  const [rows, setRows] = useState<ColorRow[]>([]);

  // Whether a shape is currently selected in PowerPoint — Replace only ever
  // scans the selected shape now (no "current slide" fallback); this drives
  // both the empty-state message and disabling Scan/OK. Starts true since
  // ColourToolsPanel's requireSelectionForReplace already guarantees a
  // selection exists before this modal ever opens; runScan (the only place
  // this is set) corrects it the moment that stops being true.
  const [hasSelection, setHasSelection] = useState(true);

  // Local text buffers for the Slide Range inputs, distinct from the
  // committed numeric rangeFrom/rangeTo (mirrors the replaceAllRgbInput /
  // replaceAllHex split used for the "Replace all" colour field above).
  // Committing a keystroke straight to rangeFrom/rangeTo on every change,
  // then displaying `String(rangeFrom).padStart(2, "0")`, reformatted the
  // field mid-typing — e.g. typing "1" then "5" for "15" got padded back to
  // "01" before the second digit landed, making the field feel uneditable.
  // Typing now only ever touches these text buffers; committing to the
  // numeric state (and re-padding the display) happens on blur/Enter.
  const [rangeFromText, setRangeFromText] = useState("01");
  const [rangeToText, setRangeToText] = useState("01");

  useEffect(() => {
    setRangeFromText(String(rangeFrom).padStart(2, "0"));
  }, [rangeFrom]);
  useEffect(() => {
    setRangeToText(String(rangeTo).padStart(2, "0"));
  }, [rangeTo]);

  const commitRangeFrom = useCallback(() => {
    const n = Number.parseInt(rangeFromText, 10);
    if (!Number.isNaN(n) && n >= 1) {
      const capped = tools.slideCount != null ? Math.min(n, tools.slideCount) : n;
      // Never let "From" land above "To" — mirrors the guard on "To"'s own commit.
      setRangeFrom(Math.min(capped, rangeTo));
    } else {
      setRangeFromText(String(rangeFrom).padStart(2, "0"));
    }
  }, [rangeFromText, rangeFrom, rangeTo, tools.slideCount]);

  const commitRangeTo = useCallback(() => {
    const n = Number.parseInt(rangeToText, 10);
    if (!Number.isNaN(n) && n >= rangeFrom) {
      setRangeTo(tools.slideCount != null ? Math.min(n, tools.slideCount) : n);
    } else {
      setRangeToText(String(rangeTo).padStart(2, "0"));
    }
  }, [rangeToText, rangeFrom, rangeTo, tools.slideCount]);

  const commitRangeOnEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") e.currentTarget.blur();
  }, []);

  // Clamp the range down once the real slide count is known — covers both
  // a stale value from before it loaded and a deck that shrank since.
  useEffect(() => {
    if (tools.slideCount == null) return;
    setRangeFrom((v) => Math.min(v, tools.slideCount!));
    setRangeTo((v) => Math.min(v, tools.slideCount!));
  }, [tools.slideCount]);

  // Resolves the "Apply to" scope into the slideFrom/slideTo/currentSlideOnly/
  // selectedShapesOnly shape replace() needs — used ONLY for the actual
  // replace execution (handleOk below). Detection is intentionally
  // decoupled from this (see runScan above) — "Apply to" decides WHERE a
  // replace acts on the colours already shown, not what gets scanned.
  const resolveScope = useCallback(
    () => resolveColorToolScope(replaceScope, rangeFrom, rangeTo),
    [replaceScope, rangeFrom, rangeTo],
  );

  // Refresh the slide count (for the range inputs) once, when the modal opens.
  useEffect(() => {
    if (open) void tools.loadSlideCount();
  }, [open]);

  // Replace only ever scans the selected shape — no fallback to the current
  // slide. "Apply to" (Current Slide/Slide Range/All Slides) only decides
  // WHERE a replace acts on the colours already shown — it never drives
  // what gets scanned to produce them, and switching it must never change
  // the list (see the effect below, which deliberately does NOT depend on
  // replaceScope). Tracks the live selection in a ref so the polling effect
  // can tell "did selection presence change" apart from "still the same",
  // without needing its own separate Office.js round trip to compare.
  const hasSelectionRef = useRef<boolean | null>(null);
  const runScan = useCallback(async (target: ColourTarget) => {
    const selected = await validateSelection();
    hasSelectionRef.current = selected;
    setHasSelection(selected);
    if (selected) {
      void tools.detectSelectedColors(target, { selectedShapesOnly: true });
    } else {
      // Nothing selected any more — the rows shown a moment ago belong to a
      // selection that no longer exists; clear them rather than leaving a
      // stale list up (tools.selectedShapeColors itself isn't re-fetched
      // here, so the effect populating `rows` from it would otherwise never
      // get a chance to clear this on its own).
      setRows([]);
    }
  }, [tools]);

  // Re-detect colours when the modal opens or the TARGET (Fill/Font/Line)
  // changes — deliberately NOT when the "Apply to" SCOPE changes (deps
  // intentionally list only `open`/`replaceTarget`, not `replaceScope`/
  // `rangeFrom`/`rangeTo`/`runScan`'s own theme-target closure — a scope
  // switch alone must never re-trigger this).
  useEffect(() => {
    if (!open) return;
    runScan(replaceTarget);
  }, [open, replaceTarget]);

  // Office.js has no selection-changed event to react to instead — the
  // modal can stay open well after the shape it opened for gets selected/
  // deselected, so this polls. Only re-scans when "is anything selected"
  // actually FLIPS (compared against the ref runScan itself maintains),
  // not on every tick — switching from a shape to nothing (or back)
  // switches which of the 2 scan bases applies; anything else (still
  // selected, still not) is a no-op.
  useEffect(() => {
    if (!open) return;
    const interval = window.setInterval(async () => {
      const hasSelection = await validateSelection();
      if (hasSelectionRef.current === hasSelection) return;
      runScan(replaceTarget);
    }, 1500);
    return () => window.clearInterval(interval);
  }, [open, replaceTarget, runScan]);

  // CSS / <input type="color"> both require a leading #.
  // normalizeHex strips it, so add it back at the UI boundary.
  const cssHex = (hex: string) => (hex.startsWith("#") ? hex : "#" + hex);

  // Populate rows from selectedShapeColors when they change (and no initialMappings)
  // — re-runs every time detection re-fires (modal open, target change, and
  // notably every "Apply to" scope change, since a colour unique to a
  // different scope needs to show up). A row already in the list keeps
  // whatever "to" colour/checked state the user already picked for it
  // instead of being rebuilt from the nearest-theme-colour suggestion — only
  // a genuinely NEW "from" colour (not seen before this rescan) gets the
  // default. Without this, switching scope (or re-scanning) discarded any
  // colour the user had already picked, resetting it back to the suggestion.
  useEffect(() => {
    if (initialMappings && initialMappings.length > 0) return;
    setRows((prevRows) => {
      const prevByFrom = new Map(prevRows.map((r) => [r.from, r]));
      return tools.selectedShapeColors.map((entry) => {
        const from = cssHex(entry.hex);
        const existing = prevByFrom.get(from);
        return {
          from,
          to: existing ? existing.to : cssHex(entry.nearest?.hex ?? entry.hex),
          checked: existing ? existing.checked : true,
          count: entry.count,
          nearestKey: entry.nearest?.key ?? null,
        };
      });
    });
  }, [tools.selectedShapeColors, initialMappings]);

  // Override rows when initialMappings prop changes
  useEffect(() => {
    if (!initialMappings || initialMappings.length === 0) return;
    const newRows: ColorRow[] = initialMappings.map((m) => ({
      from: cssHex(m.from),
      to: cssHex(m.to),
      checked: true,
      count: 0,
      nearestKey: null,
    }));
    setRows(newRows);
  }, [initialMappings]);

  // Sync replaceAllRgbInput when replaceAllHex changes via color picker
  const handleReplaceAllPickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const hex = e.target.value;
      setReplaceAllHex(hex);
      setReplaceAllRgbInput(hex);
    },
    [],
  );

  const handleReplaceAllTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setReplaceAllRgbInput(val);
      // normalizeHex never throws — it returns null for anything it can't
      // parse, so an invalid in-progress value just leaves replaceAllHex
      // (and its live swatch) at the last valid colour.
      //
      // Bug fixed here: normalizeHex strips the leading "#", but
      // replaceAllHex also feeds the native <input type="color"> below,
      // which requires a "#rrggbb" value and silently resets to black on
      // anything else — so typing a valid hex in the text field was making
      // the colour-picker swatch flash to black instead of showing the
      // colour just typed. cssHex() re-adds it, matching how every other
      // hex value in this file (the picker's own onChange, `rows[].from/to`)
      // is kept "#"-prefixed.
      const normalized = normalizeHex(val);
      if (normalized) setReplaceAllHex(cssHex(normalized));
    },
    [],
  );

  const handleRowToChange = useCallback((idx: number, hex: string) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, to: hex } : r)),
    );
  }, []);

  const handleRowChecked = useCallback((idx: number, checked: boolean) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, checked } : r)),
    );
  }, []);

  // Manual rescan — picks up colors from anything changed on the slide/selection
  // since the modal opened (new shapes, edited fills, etc.) without requiring
  // the user to close and reopen the dialog. Follows the exact same 2-way
  // rule as every other scan (see runScan) — it does NOT scan against
  // whatever "Apply to" scope happens to be selected.
  const handleRescan = useCallback(() => {
    runScan(replaceTarget);
  }, [runScan, replaceTarget]);

  const handleOk = useCallback(async () => {
    let mappings: Array<{ from: string; to: string }>;
    if (replaceAll) {
      const uniqueFromColors = [...new Set(rows.map((r) => r.from))];
      mappings = uniqueFromColors.map((from) => ({ from, to: replaceAllHex }));
    } else {
      mappings = rows
        .filter((r) => r.checked)
        .map((r) => ({ from: r.from, to: r.to }));
    }
    if (mappings.length === 0) return;

    const targets = new Set<ColourTarget>([replaceTarget]);
    const { slideFrom, slideTo, currentSlideOnly = false, selectedShapesOnly = false } = resolveScope();
    try {
      await tools.replace(mappings, targets, slideFrom, slideTo, currentSlideOnly, selectedShapesOnly);
    } catch (e) {
      logReplaceError("replace-ok-unexpected", "ReplaceOkError", e);
    }
  }, [replaceAll, replaceAllHex, rows, replaceTarget, resolveScope, tools]);

  const isReplaceAllHexValid = useMemo(
    () => normalizeHex(replaceAllRgbInput) !== null,
    [replaceAllRgbInput],
  );

  if (!open) return null;

  const checkedCount = rows.filter((r) => r.checked).length;
  const canOk =
    !tools.busy &&
    !tools.error &&
    (replaceAll ? isReplaceAllHexValid : checkedCount > 0);

  let colorsSection: React.ReactNode;
  if (tools.detectingShapeColors) {
    colorsSection = (
      <div className={styles.spinnerRow}>
        <span className={styles.spinner} />{" "}
        Detecting colors…
      </div>
    );
  } else if (rows.length === 0) {
    colorsSection = (
      <div className={styles.noColors}>
        {hasSelection
          ? "No colors detected in the selected shape."
          : "Please select a shape to replace its color."}
      </div>
    );
  } else {
    colorsSection = (
      <div className={`${styles.colorList} ${replaceAll ? styles.colorListDisabled : ""}`}>
        {rows.map((row, idx) => (
          <div key={row.from + idx} className={styles.colorRow}>
            <input
              type="checkbox"
              checked={row.checked}
              onChange={(e) => handleRowChecked(idx, e.target.checked)}
              disabled={replaceAll}
              data-testid={`replace-row-checkbox-${idx}`}
            />
            <span
              className={styles.fromSwatch}
              style={{ background: row.from }}
            />
            <div className={styles.colorInfo}>
              <span className={styles.rgbText}>
                {hexToRgbStr(row.from)}
              </span>
              <span className={styles.nearestText}>
                {formatShapeCount(row.count)}
                {row.nearestKey ? `, nearest: ${row.nearestKey}` : ""}
              </span>
            </div>
            <span className={styles.separator}>→</span>
            <input
              type="color"
              className={styles.toPicker}
              value={row.to}
              onChange={(e) => handleRowToChange(idx, e.target.value)}
              title="Pick target color"
              style={{ background: row.to }}
              disabled={replaceAll}
              data-testid={`replace-row-color-${idx}`}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog} ref={dialogRef}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Replace Colors</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close" data-testid="replace-close">
            ×
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Replace all row */}
          <div className={styles.replaceAllRow}>
            <input
              type="checkbox"
              id="replaceAll"
              checked={replaceAll}
              onChange={(e) => setReplaceAll(e.target.checked)}
              data-testid="replace-all-checkbox"
            />
            <label htmlFor="replaceAll" style={{ cursor: "pointer" }}>
              Replace all color(s) with
            </label>
            <input
              type="color"
              className={styles.colorSwatch}
              value={replaceAllHex}
              onChange={handleReplaceAllPickerChange}
              title="Pick replacement color"
              disabled={!replaceAll}
              data-testid="replace-all-color-picker"
            />
            <input
              type="text"
              className={styles.rgbInput}
              value={replaceAllRgbInput}
              onChange={handleReplaceAllTextChange}
              placeholder="#rrggbb"
              spellCheck={false}
              aria-invalid={replaceAll && !isReplaceAllHexValid}
              aria-describedby={replaceAll && !isReplaceAllHexValid ? REPLACE_ALL_HEX_ERROR_ID : undefined}
              disabled={!replaceAll}
              data-testid="replace-all-hex-input"
            />
          </div>
          {replaceAll && !isReplaceAllHexValid && (
            <div id={REPLACE_ALL_HEX_ERROR_ID} className={styles.errorInline}>
              Enter a valid hex colour, e.g. #0B5DAA
            </div>
          )}

          {/* Colors detected — always based on live selection (see runScan),
              never on the "Apply to" scope below. */}
          <div>
            <div className={styles.sectionLabel}>
              Colors detected in {SCOPE_OPTION_LABELS.selected} ({rows.length})
            </div>
            {colorsSection}
          </div>

          {/* Select what to replace */}
          <div>
            <div className={styles.sectionLabel}>Select what to replace</div>
            <div className={styles.radioGroup}>
              {(["fill", "font", "line"] as ColourTarget[]).map((t) => (
                <label
                  key={t}
                  className={`${styles.radioRow} ${replaceTarget === t ? styles.radioRowActive : ""}`}
                >
                  <input
                    type="radio"
                    name="replaceTarget"
                    value={t}
                    checked={replaceTarget === t}
                    onChange={() => setReplaceTarget(t)}
                    data-testid={`replace-target-${t}`}
                  />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </label>
              ))}
            </div>
            <div className={styles.noteText}>
              Colors auto-detected from the selected shape.
            </div>
          </div>

          {/* Apply to */}
          <div>
            <div className={styles.sectionLabel}>Apply to</div>
            <div className={styles.scopeRow}>
              {(["current", "range", "all"] as ColorToolScope[]).map((s) => (
                <label key={s} className={styles.scopeOption}>
                  <input
                    type="radio"
                    name="replaceScope"
                    value={s}
                    checked={replaceScope === s}
                    onChange={() => setReplaceScope(s)}
                    data-testid={`replace-scope-${s}`}
                  />
                  {SCOPE_OPTION_LABELS[s]}
                </label>
              ))}
            </div>
            {replaceScope === "range" && (
              <div className={styles.slideRangeRow}>
                <span>From Slide:</span>
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setRangeFrom((v) => Math.max(1, v - 1))}
                  data-testid="replace-from-decrement"
                >
                  −
                </button>
                <input
                  type="text"
                  className={styles.rangeInput}
                  value={rangeFromText}
                  onChange={(e) => setRangeFromText(e.target.value)}
                  onBlur={commitRangeFrom}
                  onKeyDown={commitRangeOnEnter}
                  data-testid="replace-from-input"
                />
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setRangeFrom((v) => {
                    const next = tools.slideCount != null ? Math.min(tools.slideCount, v + 1) : v + 1;
                    return Math.min(next, rangeTo);
                  })}
                  disabled={tools.slideCount != null && rangeFrom >= tools.slideCount}
                  data-testid="replace-from-increment"
                >
                  +
                </button>
                <span>to</span>
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setRangeTo((v) => Math.max(rangeFrom, v - 1))}
                  data-testid="replace-to-decrement"
                >
                  −
                </button>
                <input
                  type="text"
                  className={styles.rangeInput}
                  value={rangeToText}
                  onChange={(e) => setRangeToText(e.target.value)}
                  onBlur={commitRangeTo}
                  onKeyDown={commitRangeOnEnter}
                  data-testid="replace-to-input"
                />
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setRangeTo((v) => (tools.slideCount != null ? Math.min(tools.slideCount, v + 1) : v + 1))}
                  disabled={tools.slideCount != null && rangeTo >= tools.slideCount}
                  data-testid="replace-to-increment"
                >
                  +
                </button>
              </div>
            )}
          </div>

          {/* Status / done */}
          {tools.busy && (
            <>
              <div className={styles.spinnerRow}>
                <span className={styles.spinner} />{" "}
                Replacing colors…
                {tools.replaceProgress > 0 && tools.replaceProgress < 100 && (
                  <span style={{ marginLeft: 6, fontSize: "0.85em", opacity: 0.75 }}>
                    {tools.replaceProgress}%
                  </span>
                )}
              </div>
              {tools.replaceProgress > 0 && tools.replaceProgress < 100 && (
                <div style={{ height: 3, background: "#e0e0e0", borderRadius: 2, margin: "2px 0 4px" }}>
                  <div
                    style={{
                      width: `${tools.replaceProgress}%`,
                      height: "100%",
                      background: "#0B5DAA",
                      borderRadius: 2,
                      transition: "width 0.25s ease",
                    }}
                  />
                </div>
              )}
            </>
          )}
          {tools.error && !tools.busy && (
            <div className={styles.errorNotice} role="alert" data-testid="replace-error">
              {tools.error}
            </div>
          )}
          {tools.replaceDone !== null && !tools.busy && !tools.error && (
            <div className={styles.doneNotice}>
              Replaced {tools.replaceDone} instance(s).
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnScan}
            onClick={handleRescan}
            disabled={tools.detectingShapeColors || tools.busy}
            title="Re-detect colors from the current selection"
            data-testid="replace-scan"
          >
            {tools.detectingShapeColors ? "Scanning…" : "↺ Scan"}
          </button>
          <button type="button" className={styles.btnCancel} onClick={onClose} data-testid="replace-cancel">
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnOk}
            onClick={handleOk}
            disabled={!canOk}
            data-testid="replace-ok"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
